import { LazyLoadingSectionLogger, LoggingSection } from "@multiweb/logging";
import { EMPTY_OBJECT, TAB_INDEX_FOCUSABLE_ONLY } from "@multiweb/reactUtils";
import { IAttrValue, IRowData } from "@multiweb/types/contracts";
import { ObjectUtils } from "Global/Base/Utils/ObjectUtils";
import { ExtendedPropertiesType } from "UI/types";
import { DataGridSharedMethods } from "UI/UI.Controls/DataGridSharedMethods";
import { IEncAttrValue } from "../../../../Global/Base/DataTypes/IAttrValue";
import { Event } from "../../../../Global/Base/Events";
import { AttrValueReader } from "../../../../OW.Remoting/Remoting/AttrValueReader";
import { Enum } from "../../../../OW.Remoting/Remoting/DataTypes/Enums";
import { Flags } from "../../../../OW.Remoting/Remoting/DataTypes/Flags";
import { IOWObject } from "../../../../OW.Remoting/Remoting/DataTypes/IOWObject";
import { IAwaitableEvent } from "../../../UI.BaseControls/AwaitableEvent";
import { Control } from "../../../UI.BaseControls/Control";
import { IValidatable, IValidationResult } from "../../../UI.BaseControls/ControlInterfaces";
import { ElementUtils } from "../../../UI.ElementarControls/Helpers/ElementUtils";
import { createElement, registerCustomElement } from "../../../UI.ElementarControls/Utils/CustomElementUtils";
import { IHtmlElementWithResources } from "../../../UI/UI/Controls/ControlInterfaces";
import { IDataGrid } from "../../IDataGrid";
import { ICellValueChangedPrewAwaitableEvent, IDataGridSelectionChangedEvent, IDataGridSelectionChangedEventData } from "../../IEvent";
import { Column } from "../Columns/Column";
import { DOUBLE_CLICK_TIME, IDataRow, IDataRowElement, PreviewRowClickEvent, RowClickEvent } from "./IDataRow";
import { TableCell } from "./TableCell";

///////////////////////////////////////////////////////////
//  DataRow.
//  Implementation of the Class DataRow
//  Generated by Enterprise Architect
//  Created on:      25-I-2016 14:13:17
//  Original author: AndrlikP
///////////////////////////////////////////////////////////
//import {IDataGrid} from '../../IDataGrid';

export interface DataRow {
    addEventListener(type: "SelectionChanged", listener: (ev: IDataGridSelectionChangedEvent) => any, useCapture?: boolean): void;
    addEventListener(type: "SelectionChangedPreview", listener: (ev: IAwaitableEvent<boolean>) => any, useCapture?: boolean): void;
    addEventListener(type: "CellValueChangedPreview", listener: (ev: ICellValueChangedPrewAwaitableEvent) => any, useCapture?: boolean): void;
    /**
     * Událost volána okamžitě při kliku. Klasický rowClick má zpozdění nekolik MS kvůli detekci zda nedojde k double-clicku
     * @param type
     * @param listener
     * @param useCapture
     */
    addEventListener(type: "PreviewRowClick", listener: (ev: PreviewRowClickEvent) => any, useCapture?: boolean): void;
    addEventListener(type: "RowClick", listener: (ev: RowClickEvent) => any, useCapture?: boolean): void;
    addEventListener(type: string, listener: EventListenerOrEventListenerObject, useCapture?: boolean): void;
}

const logger = LazyLoadingSectionLogger.create(LoggingSection.dataGrid);

/**
 * Radek dataGridu
 * @author AndrlikP
 * @version 1.0
 * @created 25-I-2016 14:13:17
 */
export class DataRow extends Control implements IDataRowElement {
    public readonly cellValidityChanged: Event<IValidatable, IValidationResult> = new Event<IValidatable, IValidationResult>();
    public readonly selectionStateChanged: Event<IDataRow, boolean> = new Event<IDataRow, boolean>();

    protected _dataKey: string;
    private _isDeleted: boolean;
    protected _controlsCellElement: HTMLElement | undefined;
    protected _controlElement: HTMLElement | undefined;
    protected _frozenPart: HTMLElement;

    protected _clickCount: number = 0;

    public readonly isContainer: boolean = true;

    protected _dataGrid: IDataGrid<IDataRowElement>;

    private __dataCells: TableCell[];
    private _isDataRowDisposed = false;

    public get dataGrid(): IDataGrid<IDataRowElement> {
        return this._dataGrid;
    }

    public get clickCount(): number {
        if (this.checkAndLogDisposedUsage("get clickCount")) return 0;
        return this._clickCount;
    }

    public get isValid(): boolean {
        if (this.checkAndLogDisposedUsage("get isValid")) return true;

        return !this.hasAttribute("invalid");
    }

    public set isValid(v: boolean) {
        if (this.checkAndLogDisposedUsage("set isValid")) return;

        ElementUtils.toggleAttribute(this, "invalid", !v);
    }

    public get isSelected(): boolean {
        if (this.checkAndLogDisposedUsage("get isSelected")) return false;

        if (this._dataGrid.selectedRows.indexOf(this) != -1)
            return true;
        else
            return false;
    }

    public set isSelected(value: boolean) {
        if (this.checkAndLogDisposedUsage("set isSelected")) return;

        const old = this.classList.contains("selectedRecord");
        if (old == value)
            return;

        this.classList.toggle("selectedRecord", value);
        const index = this._dataGrid.selectedRows.indexOf(this);
        if (value) {
            if (index === -1)
                this._dataGrid.selectedRows.push(this);
        } else {
            if (index !== -1) {
                this._dataGrid.selectedRows.splice(index, 1);
                this._dataGrid.clearIsSelectedAllIfNeeded();
            }
        }

        this.selectionStateChanged.trigger(this, value);
    }

    /**
     * Nastavi záznam visuálně do stavu smazany
     */
    public get isDeleted(): boolean {
        if (this.checkAndLogDisposedUsage("get isDeleted")) return false;

        return this._isDeleted;
    }

    public set isDeleted(value: boolean) {
        if (this.checkAndLogDisposedUsage("set isDeleted")) return;

        this.classList.toggle("IsDeleted", value);
        this._isDeleted = value;
    }

    /**
     * Obravení radku
     */
    public get flag(): Enum | string {
        if (this.checkAndLogDisposedUsage("get flag")) return undefined;

        const flag = this.getAttribute("flag");
        if (flag)
            return Flags.get(flag);
        else
            return undefined;
    }

    public set flag(v: Enum | string) {
        if (this.checkAndLogDisposedUsage("set flag")) return;

        if (v)
            this.setAttribute("flag", v instanceof Enum ? v.getName() : v);
        else
            this.removeAttribute("flag");
    }

    /**
     * ID radku
     */
    public get dataKey(): string {
        if (this.checkAndLogDisposedUsage("get dataKey")) return this._dataKey;

        return this._dataKey.toString();
    }

    public set dataKey(v: string) {
        if (this.checkAndLogDisposedUsage("set dataKey")) return;

        this._dataKey = v;
        this.setAttribute("datakey", v);
        if (this._controlElement)
            this._controlElement.setAttribute("datakey", v);
    }

    public get controlsCellElement(): HTMLElement | undefined {
        if (this.checkAndLogDisposedUsage("get controlsCellElement")) return undefined;

        return this._controlsCellElement;
    }

    public get dataCells(): readonly TableCell[] {
        if (this.checkAndLogDisposedUsage("get dataCells")) return [];

        return this.__dataCells;
    }

    protected get _dataCells(): TableCell[] {
        if (this.checkAndLogDisposedUsage("get _dataCells")) return [];

        return this.__dataCells;
    }

    protected set _dataCells(v: TableCell[]) {
        if (this.checkAndLogDisposedUsage("set _dataCells")) return;

        this.__dataCells = v;
    }

    protected static override get observedAttributes(): string[] {
        return ["flag", "data-instanceid"].concat(Control.observedAttributes);
    }

    //#region Methods

    public async setData(attrName: string, value: IAttrValue): Promise<void> {
        if (this.checkAndLogDisposedUsage("setData")) return;

        for (const cell of this.dataCells) {
            if (cell.column.withoutAttribute) {
                continue;
            }

            // let currentAttrname = ObjectUtils.removeTypeCasting(cell.column.attrName) === ObjectUtils.removeTypeCasting(attrName) ? ObjectUtils.removeTypeCasting(cell.column.attrName) : ObjectUtils.removeTypeCasting(cell.column.fullAttrName)

            if (ObjectUtils.removeTypeCasting(cell.column.attrName) === ObjectUtils.removeTypeCasting(attrName)) {
                const val = AttrValueReader.read(value);
                if (!(value instanceof IEncAttrValue) && (val == null || (val instanceof IOWObject && val.id == -666)))
                    value = null;

                cell.dataSet = value instanceof Array ? value : [value];
            } else if (cell.column.attrName.indexOf(".") !== -1) {
                const attrNames = cell.column.attrName.split(".");
                const cellAttrName = ObjectUtils.removeTypeCasting(attrNames[0]);

                if (cellAttrName === attrName) {
                    try {
                        const val = AttrValueReader.read(value);
                        if (val instanceof IOWObject) {
                            const attr = cell.column.attrName.substring(cell.column.attrName.indexOf(".") + 1);
                            let data = await val.getValue(attr);
                            if (!(data instanceof IEncAttrValue) && (data == null || (data instanceof IOWObject && val.id == -666)))
                                data = " ";
                            cell.dataSet = [data];
                        } else {
                            cell.dataSet = [value];
                        }
                    } catch (e) {
                        logger.error(`Unable to get value of an attribute '${cell.column.attrName}'`); //`
                    }
                }
            }
        }
    }

    protected _cleanRowElement(): void {
        this.innerHTML = "";
    }

    /**
     * Ziniciuje nový řádek
     * @param dataRow Data k zobrazení v řádku
     * @param ownerGrid DataGrid, ke kterému řádek patří
     * @param controlElement Volitelný element s ovládacími prvky, který se vloží do nulté buňky řádku
     */
    public init(rowData: IRowData, ownerGrid: IDataGrid<IDataRowElement>, controlElement: HTMLElement): void {
        if (this.checkAndLogDisposedUsage("init")) return;

        this._init(rowData, ownerGrid, controlElement);
        this._initCells(rowData);
        this._registerRowClickEvent();
    }

    /**
     * Základní inicializace vlastností řádku
     * @param dataRow řádek
     * @param ownerGrid rodičovský DataGrid
     * @param controlElement element s ovládacími prvky, který se vloží do nulté buňky řádku
     */
    protected _init(rowData: IRowData, ownerGrid: IDataGrid<IDataRowElement>, controlElement: HTMLElement): void {
        if (this.checkAndLogDisposedUsage("_init")) return;

        this._dataGrid = ownerGrid;
        this._dataCells = new Array<TableCell>();
        this.dataKey = rowData[this._dataGrid.dataSource.dataKey].valueOf()?.toString();
        if (this._dataKey == undefined) {
            throw Error("DataRow has invalid dataKey");
        }

        const frozenRowHelper = createElement({ classes: "frozen-part-helper" });
        this._frozenPart = createElement({ classes: "frozen-part" });
        frozenRowHelper.appendChild(this._frozenPart);
        this.insertAdjacentElement("afterbegin", frozenRowHelper);

        if (controlElement) {
            this._controlElement = controlElement;
            this._controlElement.addEventListener("widthChanged", () => {
                this.dispatchEvent(new CustomEvent("controlElementWidthChanged", {}));
            });
        }
    }

    /**
     * Inicializace datových buněk řádku
     * @param dataRow řádek
     * @param frozenCount počet řádku, ktery jsou fixni (budou vykresleny pouze ty) pokud je číslo menší jak nula budou vykresleny všechny
     * @param columns sloupce k inicializaci... pokud je null, tak se použijí sloupce z dataGridu
     */
    protected _initCells(dataRow: IRowData, _frozenCount?: number, columns?: Column[]): void {
        if (this.checkAndLogDisposedUsage("_initCells")) return;

        if (columns === null || columns === undefined) columns = this._dataGrid.columns; // columns ??= this._dataGrid.columns
        const dataRowCaseInsensitive = ObjectUtils.getPropertiesCaseInsensitive<IAttrValue>(dataRow ?? {});

        let cellIndex = this._dataCells.length;
        columns.forEach(column => {
            const data: Array<IAttrValue> = [];

            if (!column.withoutAttribute) {
                data.push(...column.attrNames.map(attrName => dataRowCaseInsensitive.get(attrName)));
            }

            const cell = new TableCell(this._dataGrid, this, cellIndex, column, data);
            cell.validityChanged.add((sender, validity) => {
                this._onCellValidityChanged();
                this.cellValidityChanged.trigger(sender, validity);
            });
            this._dataCells.push(cell);
            cellIndex++;
        });
    }

    public addCells(columns: Column[]): void {
        this._initCells(null, undefined, columns);
    }

    protected _createNoEditableCells(): void {
        if (this.checkAndLogDisposedUsage("_createNoEditableCells")) return;

        this._frozenPart.appendChild(this._createFirstCell());

        for (const cell of this.dataCells) {
            if (cell.column.visible) {
                cell.toEdit(false);
                const newCell = cell.render();
                if (newCell) //sloupec mohl změnit viditelnost a render vrátil null.
                    this.appendChild(newCell);
            }
        }
    }

    /**
     * Kontrola zda se muze zmenit vyber radku. Akce muze byt zablokovana pokud je jiny seznam rozeditovany a neulozeny.
     */
    protected async _checkIfCanSelectionChange(evt: MouseEvent = null): Promise<boolean> {
        if (this.checkAndLogDisposedUsage("_checkIfCanSelectionChange")) return true;

        if (this.isSelected && !this._canChangeSelectedRowMultiSelect(evt))
            return true;

        return await this._canChangeSelectedRow();
    }

    /**
     * Subkontrola zda se muze zmenit vyber radku.
     */
    protected _canChangeSelectedRowMultiSelect(evt: MouseEvent = null): boolean {
        if (this.checkAndLogDisposedUsage("_canChangeSelectedRowMultiSelect")) return false;

        return (this._dataGrid.multiSelect && evt && !evt.ctrlKey && !evt.shiftKey && (this._dataGrid.selectedRows.length > 1));
    }

    /** Čeká na potvrzení od uživatele při rozeditovaném formu, zda je možné provést změnu řádku */
    protected async _canChangeSelectedRow(): Promise<boolean> {
        if (this.checkAndLogDisposedUsage("_canChangeSelectedRow")) return true;

        return DataGridSharedMethods.canChangeSelectedRow(this);
    }

    /**
     * Vybere záznam v případě, že lze tuto akci provést (např. není rozeditovaný jiný seznam)
     * @param withFocus Zda má být řádek vybrán s nastavením focusu na seznam. Využívá se při výběru řádku během otevírání menu
     */
    public async selectWithCheckChanges(withFocus: boolean = true): Promise<void> {
        if (this.checkAndLogDisposedUsage("selectWithCheckChanges")) return;

        if (!(await this._checkIfCanSelectionChange()))
            return;

        if (!this.isSelected || (this.isSelected && (this._dataGrid.selectedRows.length > 1))) {
            if (withFocus) { // Bez toho by šlo např. při otevření kontextového menu i nadále měnit vybraný záznam pomocí klávesnice
                this._dataGrid.focus();
            }

            this._dataGrid.isSelectedAll = false;
            this._dataGrid.clearSelectedRows();
            this.isSelected = true;
            this._dataGrid.lastClickedRow = this;

            if (this._dataGrid.selectedRows.length == 1) {
                this._dispatchSelectionChangeEvent(this.dataKey, { doubleClick: false, clickedRow: this });
            } else { // neni vybrany zadny radek nebo oznaceno vice zaznamu
                this._dispatchSelectionChangeEvent("-1", { doubleClick: false, clickedRow: this });
            }
        }
    }

    public select(): void {
        if (this.checkAndLogDisposedUsage("select")) return;

        const oldSel = this.isSelected;
        //const oldEdit = this.DataGrid.LastClickedRow.IsEdited;

        this._dataGrid.focus();
        this._dataGrid.isSelectedAll = false;
        this._dataGrid.clearSelectedRows();
        this.isSelected = true;
        this._dataGrid.lastClickedRow = this;

        if (!oldSel)
            this._dispatchSelectionChangeEvent(this.dataKey, { doubleClick: false, clickedRow: this });
    }

    /**
     * Renders the control to the specified HTML writer.
     */
    public render(): HTMLElement {
        if (this.checkAndLogDisposedUsage("render")) return undefined;

        this._cleanRowElement();
        this._createNoEditableCells();

        return this as HTMLElement;
    }

    /**
     * Nastaví události po kliknutí na řádek.
     * @param [row=this] Řádek, který bude předán ve vyvolaných událostech jako iniciátor.
     * Parametr využívají frozen řádky a předávají zde své přidružené řádky.
     */
    protected _registerRowClickEvent(row: IDataRow = this): void {
        if (this.checkAndLogDisposedUsage("_registerRowClickEvent")) {
            return;
        }
        this.unregisterEventListener("click");
        this.registerEventListenerWithReregister("click", (e: MouseEvent) => {
            this._clickCount++;
            const originalEvent = e;
            this.callEvent("previewrowclick", { originalEvent: originalEvent, row } as PreviewRowClickEvent);
            self.setTimeout(() => {
                this.callEvent("rowclick", { originalEvent: originalEvent, row, clickCount: this._clickCount } as RowClickEvent);
                this._clickCount = 0;
            }, DOUBLE_CLICK_TIME);
        });
    }

    protected _doubleClickEvt(): void {
        if (this.checkAndLogDisposedUsage("_doubleClickEvt")) return;

        this._dispatchSelectionChangeEvent(this.dataKey, { doubleClick: true, clickedRow: this });
    }

    protected _createFirstCell(): HTMLTableCellElement {
        if (this.checkAndLogDisposedUsage("_createFirstCell")) return undefined;

        const tableCell = createElement({ type: "td", classes: "tableRowCheckbox" }) as HTMLTableCellElement;
        if (!this._dataGrid.renderControlElement) {
            tableCell.style.width = "0px";
            return tableCell;
        }

        // element s ovládacími prvky k datovému záznamu
        if (this._controlElement)
            tableCell.appendChild(this._controlElement);

        this._controlsCellElement = tableCell;

        return tableCell;
    }

    public setItemType(): void {
        // Prázdná metoda
    }

    public getExtendedProperties(attrName: string): ExtendedPropertiesType {
        if (this._dataGrid.getExtendedProperties)
            return this._dataGrid.getExtendedProperties(attrName);

        return EMPTY_OBJECT;
    }

    protected _onCellValidityChanged(): void {
        if (this.checkAndLogDisposedUsage("_onCellValidityChanged")) return;

        let valid = true;
        for (const cell of this.dataCells)
            valid = cell.isValid && valid;

        this.isValid = valid;
    }

    /**
     * Zkontroluje, zda je řádek disposed a pokud ano zaloguje warning o volání metody nad disposed řádkem.
     *
     * Tato metoda a obecně kontroly na dispose by neměly být nutné po odstranění příčin, kvůli kterým se pracuje i nadále s disposed objekty.
     * Pro řešení příčin je vytvořený task 33553.
     *
     * @param methodName Název metody, kde se provádí kontrola. Slouží pro logování.
     * @return true, pokud je řádek disposed, jinak false.
     */
    protected checkAndLogDisposedUsage(methodName: string): boolean {
        const disposed = this.isDisposed();
        if (disposed) {
            logger.warn(`Calling ${methodName} on disposed ${this.constructor.name}!`);
        }
        return disposed;
    }

    public isDisposed(): boolean {
        return this._isDataRowDisposed;
    }

    public override dispose(): void {
        if (this._dataGrid) {
            // TODO Provizorní oprava, selected rows by se mělo čistit odjinud. Opravit příčinu práce s disposed řádky v rámci tasku 33553.
            this._dataGrid.selectedRows = this._dataGrid.selectedRows.filter((row) => row !== this);
        }

        this.cellValidityChanged.removeAll();
        this.selectionStateChanged.removeAll();
        this._controlsCellElement = undefined;
        if (this._controlElement) {
            if (this._controlElement instanceof IHtmlElementWithResources) {
                this._controlElement.dispose();
            }
            this._controlElement = undefined;
        }
        this.__dataCells?.forEach((cell) => cell.dispose());
        super.dispose();
        this._isDataRowDisposed = true;
    }

    //#endregion Methods

    //#region HTMLElement
    protected override _constructor(): void {
        super._constructor();
        this.classList.add("DataRow");
        this.tabIndex = TAB_INDEX_FOCUSABLE_ONLY;
    }

    protected override attributeChangedCallback(attrName: string, oldVal: string, newVal: string): void {
        if (this.checkAndLogDisposedUsage("attributeChangedCallback")) return;

        super.attributeChangedCallback(attrName, oldVal, newVal);

        switch (attrName) {
            case "flag":
                if (oldVal)
                    this.classList.remove(oldVal);

                if (newVal && newVal != "fNone") {
                    this.classList.add("colored", newVal);
                } else {
                    this.classList.remove("colored");
                }
                break;
        }
    }

    /**
     * Vyvola novou udalost, ktera se pouziva pri zmene vybranych radku
     * @param rowID -
     * @param data -
     */
    protected _dispatchSelectionChangeEvent(rowID: string, data: any): void {
        if (this.checkAndLogDisposedUsage("_dispatchSelectionChangeEvent")) return;

        this.callEvent("SelectionChanged", { dataGrid: this._dataGrid, rowID: rowID, data: data } as IDataGridSelectionChangedEventData, true, true);
    }

    protected _dispatchDoubleClickEvent(selectionByDoubleCLick?: boolean, rowID: string = null): void {
        if (this.checkAndLogDisposedUsage("_dispatchDoubleClickEvent")) return;

        const eventArgs: IDoubleClickEventArgs = { selectionByDoubleClick: selectionByDoubleCLick };
        if (rowID)
            eventArgs.rowID = rowID;
        this.callEvent("DoubleClick", eventArgs, true, true);
    }
    //#endregion HTMLElement
} //end DataRow

export interface IDoubleClickEventArgs {
    selectionByDoubleClick: boolean;
    rowID?: string;
}

registerCustomElement("tsc-datarow", DataRow);
